import React, { useState, useEffect } from 'react';
import {
  FiPieChart,
  FiFilter,
  FiClipboard,
  FiCheckCircle,
  FiXCircle,
  FiActivity,
  FiClock,
  FiAlertTriangle,
  FiDollarSign,
  FiTrendingUp,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import Api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import { PageComponent, Card, Select, Skeletons, MultiSelect, DatePeriodFilter, getDateParams } from '../../../components';
import '../../CompanyManager/style.css';
import './style.css';

const STATUS_LABELS = {
  em_execucao: 'Em Execução',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

const SERVICE_TYPE_LABELS = {
  cortesia: 'Cortesia',
  terceirizado: 'Terceirizado',
  padrao: 'Padrão',
};

const REQUEST_TYPE_LABELS = {
  corretiva: 'Corretiva',
  preventiva: 'Preventiva',
};

const MONTH_LABELS = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function formatDuration(minutes) {
  if (!minutes) return '0min';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function KpiCard({ icon: Icon, label, value, color, subtitle }) {
  return (
    <div className={`so-kpi-card ${color || ''}`}>
      <div className="so-kpi-icon">
        <Icon size={20} />
      </div>
      <div className="so-kpi-data">
        <span className="so-kpi-value">{value}</span>
        <span className="so-kpi-label">{label}</span>
        {subtitle && <span className="so-kpi-subtitle">{subtitle}</span>}
      </div>
    </div>
  );
}

function CustomPieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <div className="so-chart-tooltip">
        <span className="so-chart-tooltip-label">{payload[0].name}</span>
        <span className="so-chart-tooltip-value">{payload[0].value}</span>
      </div>
    );
  }
  return null;
}

function CustomBarTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const parts = label ? label.split('/') : [];
    const monthLabel = parts.length === 2 ? `${MONTH_LABELS[parts[0]] || parts[0]}/${parts[1]}` : label;
    return (
      <div className="so-chart-tooltip">
        <span className="so-chart-tooltip-label">{monthLabel}</span>
        {payload.map((entry, i) => (
          <div key={i} className="so-chart-tooltip-row">
            <span className="so-chart-tooltip-dot" style={{ background: entry.color }} />
            <span>{STATUS_LABELS[entry.dataKey] || entry.dataKey}: {entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

export default function ServiceOrderOverview() {
  const permissions = usePermission('service_orders');
  const [selectedCompany] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('selectedCompany'));
    } catch (e) {
      return null;
    }
  });

  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [periodMode, setPeriodMode] = useState('last12');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (!companies || companies.length === 0) return;
    loadDashboard();
  }, [companyFilterValues, companies, periodMode, currentDate, serviceTypeFilter, requestTypeFilter]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadDashboard() {
    if (!selectedCompany?.id && (!companies || companies.length === 0)) return;
    setLoading(true);
    try {
      const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);

      const allValue = 'all';
      const specificIds = companyFilterValues.filter(v => v !== allValue);
      const hasAllSelected = companyFilterValues.includes(allValue);

      const companyIds =
        hasAllSelected || specificIds.length === 0
          ? (companies.length > 0 ? companies.map(c => c.id) : [selectedCompany.id])
          : specificIds.map(id => Number(id));

      const merged = {
        kpis: { totalOrders: 0, activeOrders: 0, inactiveOrders: 0, totalExecutions: 0, inProgress: 0, finished: 0, cancelled: 0, overdueOrders: 0, totalValue: 0, avgDurationMinutes: 0, completionRate: 0 },
        timeline: [],
        serviceTypeDistribution: [],
        requestTypeDistribution: [],
        topExecutors: [],
        recentExecutions: [],
      };

      const results = await Promise.all(
        companyIds.map(async cId => {
          const params = {};
          if (startDate) params.startDate = startDate;
          if (endDate) params.endDate = endDate;
          if (serviceTypeFilter !== 'all') params.service_type = serviceTypeFilter;
          if (requestTypeFilter !== 'all') params.request_type = requestTypeFilter;
          const dashboardRes = await Api.getServiceOrderDashboard(cId, params);
          const execsLight = await Api.getServiceOrderExecutionsLight(cId, params);
          return { dashboard: dashboardRes, executions: Array.isArray(execsLight) ? execsLight : [] };
        })
      );

      const timelineMap = {};
      const serviceTypeMap = {};
      const requestTypeMap = {};
      const executorMap = {};
      const allExecutions = [];

      results.forEach(({ dashboard: data, executions }) => {
        if (!data || data.error) return;

        // Merge KPIs (order-level, not dependentes de período)
        merged.kpis.totalOrders += data.kpis?.totalOrders || 0;
        merged.kpis.activeOrders += data.kpis?.activeOrders || 0;
        merged.kpis.inactiveOrders += data.kpis?.inactiveOrders || 0;
        merged.kpis.overdueOrders += data.kpis?.overdueOrders || 0;
        merged.kpis.totalValue += data.kpis?.totalValue || 0;

        // Collect executions for period-based timeline
        if (Array.isArray(executions)) {
          allExecutions.push(
            ...executions.filter(e => e.started_at)
          );
        }

        // Merge distributions
        (data.serviceTypeDistribution || []).forEach(d => {
          serviceTypeMap[d.name] = (serviceTypeMap[d.name] || 0) + d.value;
        });
        (data.requestTypeDistribution || []).forEach(d => {
          requestTypeMap[d.name] = (requestTypeMap[d.name] || 0) + d.value;
        });

        // Merge executors
        (data.topExecutors || []).forEach(e => {
          executorMap[e.name] = (executorMap[e.name] || 0) + e.count;
        });

        // Merge recent executions
        merged.recentExecutions.push(...(data.recentExecutions || []));
      });

      // Build period-faithful timeline and KPIs from executions
      const startDateObj = startDate ? new Date(startDate) : null;
      const endDateObj = endDate ? new Date(endDate) : null;

      allExecutions.forEach(e => {
        if (!e.started_at) return;
        const d = new Date(e.started_at);
        if (startDateObj && d < startDateObj) return;
        if (endDateObj) {
          const endCopy = new Date(endDateObj);
          endCopy.setHours(23, 59, 59, 999);
          if (d > endCopy) return;
        }
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!timelineMap[key]) {
          timelineMap[key] = { month: key, finalizado: 0, cancelado: 0, em_execucao: 0 };
        }
        if (e.status && timelineMap[key][e.status] !== undefined) {
          timelineMap[key][e.status] += 1;
        }
      });

      const totalExecutions = allExecutions.length;
      const inProgressCount = allExecutions.filter(e => e.status === 'em_execucao').length;
      const finishedCount = allExecutions.filter(e => e.status === 'finalizado').length;
      const cancelledCount = allExecutions.filter(e => e.status === 'cancelado').length;

      merged.kpis.totalExecutions = totalExecutions;
      merged.kpis.inProgress = inProgressCount;
      merged.kpis.finished = finishedCount;
      merged.kpis.cancelled = cancelledCount;

      const finishedExecs = allExecutions.filter(e => e.status === 'finalizado' && e.started_at && e.finished_at);
      if (finishedExecs.length > 0) {
        const totalMinutes = finishedExecs.reduce((sum, e) => {
          return sum + (new Date(e.finished_at) - new Date(e.started_at)) / 60000;
        }, 0);
        merged.kpis.avgDurationMinutes = Math.round(totalMinutes / finishedExecs.length);
      } else {
        merged.kpis.avgDurationMinutes = 0;
      }

      merged.kpis.completionRate = totalExecutions > 0
        ? Math.round((finishedCount / totalExecutions) * 100)
        : 0;

      merged.timeline = Object.values(timelineMap).sort((a, b) => a.month.localeCompare(b.month));
      merged.serviceTypeDistribution = Object.entries(serviceTypeMap).map(([name, value]) => ({
        name: SERVICE_TYPE_LABELS[name] || name,
        value,
      }));
      merged.requestTypeDistribution = Object.entries(requestTypeMap).map(([name, value]) => ({
        name: REQUEST_TYPE_LABELS[name] || name,
        value,
      }));
      merged.topExecutors = Object.entries(executorMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      merged.recentExecutions = merged.recentExecutions
        .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
        .slice(0, 10);

      setDashboard(merged);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dashboard.');
    } finally {
      setLoading(false);
    }
  }

  const kpis = dashboard?.kpis || {};

  const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;

  const rawTimeline = dashboard?.timeline || [];
  const filteredTimeline = rawTimeline
    .map(t => {
      let finalizado = t.finalizado || 0;
      let em_execucao = t.em_execucao || 0;
      let cancelado = t.cancelado || 0;

      if (statusFilter === 'finalizado') {
        em_execucao = 0;
        cancelado = 0;
      } else if (statusFilter === 'em_execucao') {
        finalizado = 0;
        cancelado = 0;
      } else if (statusFilter === 'cancelado') {
        finalizado = 0;
        em_execucao = 0;
      }

      return {
        ...t,
        finalizado,
        em_execucao,
        cancelado,
      };
    })
    .filter(t => {
      const total = (t.finalizado || 0) + (t.em_execucao || 0) + (t.cancelado || 0);
      if (total === 0) return false;
      if (!startDateObj || !endDateObj) return true;
      if (!t.month || typeof t.month !== 'string') return true;
      const [y, m] = t.month.split('-').map(Number);
      if (!y || !m) return true;
      const monthDate = new Date(y, m - 1, 15);
      return monthDate >= startDateObj && monthDate <= endDateObj;
    });

  const timeline = filteredTimeline.map(t => ({
    ...t,
    label: `${t.month.split('-')[1]}/${t.month.split('-')[0].slice(2)}`,
  }));

  const filteredServiceTypeDistribution =
    (dashboard?.serviceTypeDistribution || []).filter(d => {
      if (serviceTypeFilter === 'all') return true;
      const expectedLabel = SERVICE_TYPE_LABELS[serviceTypeFilter] || serviceTypeFilter;
      return d.name === expectedLabel;
    });

  const filteredRequestTypeDistribution =
    (dashboard?.requestTypeDistribution || []).filter(d => {
      if (requestTypeFilter === 'all') return true;
      const expectedLabel = REQUEST_TYPE_LABELS[requestTypeFilter] || requestTypeFilter;
      return d.name === expectedLabel;
    });

  const filteredRecentExecutions = (dashboard?.recentExecutions || []).filter(exec => {
    if (statusFilter !== 'all' && exec.status !== statusFilter) return false;
    if (serviceTypeFilter !== 'all' && exec.serviceOrder?.service_type !== serviceTypeFilter) return false;
    if (requestTypeFilter !== 'all' && exec.serviceOrder?.request_type !== requestTypeFilter) return false;
    if (startDateObj && endDateObj && exec.started_at) {
      const d = new Date(exec.started_at);
      if (d < startDateObj || d > endDateObj) return false;
    }
    return true;
  });

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiPieChart size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        <div className="manager-header">
          <div className="manager-header-title">
            <div className="manager-header-icon">
              <FiPieChart size={22} />
            </div>
            <div>
              <h1>Visão Geral - Ordens de Serviço</h1>
              <p>Dashboard com indicadores, gráficos e métricas de execuções.</p>
            </div>
          </div>
        </div>

        {loading && !hasLoadedOnce && (
          <Skeletons.CompanyManagerSkeleton />
        )}

        {(!loading || hasLoadedOnce) && (
          <Card className="manager-filters-card">
            <div className="manager-filters-header">
              <div className="manager-filters-title">
                <FiFilter size={16} />
                <span>Filtros</span>
              </div>
            </div>
            <div className="manager-filters-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              <div className="manager-filter-group">
                <DatePeriodFilter
                  periodMode={periodMode}
                  setPeriodMode={setPeriodMode}
                  currentDate={currentDate}
                  setCurrentDate={setCurrentDate}
                  customRange={customPeriod}
                  setCustomRange={setCustomPeriod}
                />
              </div>
              <div className="manager-filter-group">
                <label className="manager-filter-label">Empresa</label>
                <MultiSelect
                  id="filter-company-dashboard"
                  options={[
                    { value: 'all', label: 'TODAS' },
                    ...companies.map(c => ({
                      value: String(c.id),
                      label: c.corporate_name || c.trade_name || c.name || c.razao_social || c.nome_fantasia || `Empresa ${c.id}`,
                    })),
                  ]}
                  value={companyFilterValues}
                  onChange={(newValues) => {
                    const allValue = 'all';
                    const currentSelected = companyFilterValues;
                    const isAllSelected = newValues.includes(allValue);
                    const wasAllSelected = currentSelected.includes(allValue);

                    let nextValues;

                    if (isAllSelected && !wasAllSelected) {
                      const allIds = companies.map(c => String(c.id));
                      nextValues = [allValue, ...allIds];
                    } else if (!isAllSelected && wasAllSelected) {
                      nextValues = [];
                    } else if (wasAllSelected && newValues.length < currentSelected.length) {
                      const remaining = newValues.filter(v => v !== allValue);
                      nextValues = remaining;
                    } else {
                      const specificIds = newValues.filter(v => v !== allValue);
                      if (specificIds.length === companies.length && companies.length > 0) {
                        nextValues = [allValue, ...specificIds];
                      } else {
                        nextValues = specificIds;
                      }
                    }

                    setCompanyFilterValues(nextValues);
                  }}
                  searchable
                />
              </div>
              <div className="manager-filter-group">
                <label className="manager-filter-label">Status da Execução</label>
                <Select
                  id="so-status-filter"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'em_execucao', label: STATUS_LABELS.em_execucao },
                    { value: 'finalizado', label: STATUS_LABELS.finalizado },
                    { value: 'cancelado', label: STATUS_LABELS.cancelado },
                  ]}
                  placeholder="SELECIONE..."
                />
              </div>
              <div className="manager-filter-group">
                <label className="manager-filter-label">Tipo de Serviço</label>
                <Select
                  id="so-service-type-filter"
                  value={serviceTypeFilter}
                  onChange={e => setServiceTypeFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'cortesia', label: SERVICE_TYPE_LABELS.cortesia },
                    { value: 'terceirizado', label: SERVICE_TYPE_LABELS.terceirizado },
                    { value: 'padrao', label: SERVICE_TYPE_LABELS.padrao },
                  ]}
                  placeholder="SELECIONE..."
                />
              </div>
              <div className="manager-filter-group">
                <label className="manager-filter-label">Tipo de Solicitação</label>
                <Select
                  id="so-request-type-filter"
                  value={requestTypeFilter}
                  onChange={e => setRequestTypeFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'corretiva', label: REQUEST_TYPE_LABELS.corretiva },
                    { value: 'preventiva', label: REQUEST_TYPE_LABELS.preventiva },
                  ]}
                  placeholder="SELECIONE..."
                />
              </div>
            </div>
          </Card>
        )}

        {!loading && dashboard ? (
          <>
            {/* KPI Cards */}
            <div className="so-dashboard-kpis">
              <KpiCard icon={FiClipboard} label="Total de OS" value={kpis.totalOrders} color="blue" />
              <KpiCard icon={FiActivity} label="OS Ativas" value={kpis.activeOrders} color="green" />
              <KpiCard icon={FiClock} label="Em Execução" value={kpis.inProgress} color="orange" />
              <KpiCard icon={FiCheckCircle} label="Finalizadas" value={kpis.finished} color="green" />
              <KpiCard icon={FiXCircle} label="Canceladas" value={kpis.cancelled} color="red" />
              <KpiCard icon={FiTrendingUp} label="Taxa de Conclusão" value={`${kpis.completionRate}%`} color="blue" />
              <KpiCard icon={FiAlertTriangle} label="OS Atrasadas" value={kpis.overdueOrders} color={kpis.overdueOrders > 0 ? 'red' : ''} />
              <KpiCard icon={FiDollarSign} label="Valor Total OS" value={formatCurrency(kpis.totalValue)} color="green" subtitle={`Duração média: ${formatDuration(kpis.avgDurationMinutes)}`} />
            </div>

            {/* Timeline Chart */}
            <Card className="so-dashboard-chart-card">
              <h3 className="so-dashboard-chart-title">Fluxo de Execuções</h3>
              <div className="so-dashboard-chart-wrapper">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={timeline} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend
                      formatter={(value) => STATUS_LABELS[value] || value}
                      wrapperStyle={{ fontSize: 12 }}
                    />
                    <Bar dataKey="finalizado" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="em_execucao" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="cancelado" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Distribution Charts Row */}
            <div className="so-dashboard-charts-row">
              {/* Status Distribution */}
              {kpis.totalExecutions > 0 && (
                <Card className="so-dashboard-chart-card">
                  <h3 className="so-dashboard-chart-title">Distribuição de Status</h3>
                  <div className="so-dashboard-chart-wrapper so-dashboard-pie-wrapper">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { key: 'finalizado', name: 'Finalizadas', value: kpis.finished },
                            { key: 'em_execucao', name: 'Em Execução', value: kpis.inProgress },
                            { key: 'cancelado', name: 'Canceladas', value: kpis.cancelled },
                          ]
                            .filter(d => d.value > 0)
                            .filter(d => statusFilter === 'all' || d.key === statusFilter)}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                        >
                          <Cell fill="#10b981" />
                          <Cell fill="#3b82f6" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              )}

              {/* Service Type Distribution */}
              {filteredServiceTypeDistribution.length > 0 && (
                <Card className="so-dashboard-chart-card">
                  <h3 className="so-dashboard-chart-title">Tipo de Serviço</h3>
                  <div className="so-dashboard-chart-wrapper so-dashboard-pie-wrapper">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={filteredServiceTypeDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                        >
                          {filteredServiceTypeDistribution.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              )}

              {/* Request Type Distribution */}
              {filteredRequestTypeDistribution.length > 0 && (
                <Card className="so-dashboard-chart-card">
                  <h3 className="so-dashboard-chart-title">Tipo de Solicitação</h3>
                  <div className="so-dashboard-chart-wrapper so-dashboard-pie-wrapper">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={filteredRequestTypeDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                        >
                          {filteredRequestTypeDistribution.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomPieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              )}
            </div>

            {/* Bottom Row: Top Executors + Recent Executions */}
            <div className="so-dashboard-bottom-row">
              {/* Top Executors */}
              {dashboard.topExecutors.length > 0 && (
                <Card className="so-dashboard-chart-card">
                  <h3 className="so-dashboard-chart-title">Top Executores (finalizadas)</h3>
                  <div className="so-dashboard-executors-list">
                    {dashboard.topExecutors.map((exec, i) => (
                      <div key={i} className="so-dashboard-executor-item">
                        <div className="so-dashboard-executor-rank">{i + 1}</div>
                        <div className="so-dashboard-executor-info">
                          <span className="so-dashboard-executor-name">{exec.name}</span>
                          <div className="so-dashboard-executor-bar-wrapper">
                            <div
                              className="so-dashboard-executor-bar"
                              style={{ width: `${(exec.count / dashboard.topExecutors[0].count) * 100}%` }}
                            />
                          </div>
                        </div>
                        <span className="so-dashboard-executor-count">{exec.count}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Recent Executions */}
              <Card className="so-dashboard-chart-card">
                <h3 className="so-dashboard-chart-title">Execuções Recentes</h3>
                {filteredRecentExecutions.length > 0 ? (
                  <div className="so-recent-list">
                    {filteredRecentExecutions.map(exec => (
                      <div key={exec.id} className="so-recent-item">
                        <div className="so-recent-info">
                          <span className="so-recent-title">
                            {exec.serviceOrder?.target_name || `OS #${exec.service_order_id}`}
                          </span>
                          <span className="so-recent-subtitle">
                            {exec.executor_name || '-'} | {formatDate(exec.started_at)}
                          </span>
                        </div>
                        <span className={`so-execution-status ${exec.status}`}>
                          {STATUS_LABELS[exec.status] || exec.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="so-dashboard-empty">Nenhuma execução registrada.</p>
                )}
              </Card>
            </div>
          </>
        ) : (
          //está aparecendo com a tela carregando
          !loading === 0 ? (
            <p className="so-dashboard-empty">Nenhum dado encontrado.</p>
          ) : null
        )}
      </div>
    </PageComponent>
  );
}
